import {css, StyleSheet} from 'aphrodite';
import React, {useRef} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import {GRAY_DARK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {InputChangeEvent} from 'util/Types';

import {
  FilterByOriginLookupStepInfo,
  FilterByOriginLookupStepSpec,
  GenericLookupStepInfo,
  LookupStepInfo,
  LookupStepType,
  ModifierLookupStepInfo,
  ModifierLookupStepType,
} from './LookupConfigModalConstants';
import {getLookupStepInfoWithType} from './LookupConfigUtils';

interface DummyModifierFormProps<StepInfoType> {
  lookupStepInfos: Array<StepInfoType>;
  stepName: string;
  onChange: (infos: Array<StepInfoType>) => void;
  disabled: boolean;
  key: string;
  isProcessOutputContext: boolean;
}

function FilterByOriginForm(
  props: DummyModifierFormProps<GenericLookupStepInfo<FilterByOriginLookupStepSpec>>
): JSX.Element {
  assert(props.lookupStepInfos.length <= 1, 'FilterByOriginForm received too many lookup steps');
  const filterByOriginStepInfo = props.lookupStepInfos[0];
  if (filterByOriginStepInfo) {
    assert(
      filterByOriginStepInfo.lookupStep.type === LookupStepType.FILTER_BY_ORIGIN,
      'FilterByOriginForm received a lookup step that was not the correct type'
    );
  }

  const filterByOrigin = Boolean(filterByOriginStepInfo);

  const checkboxOnChange = (e: InputChangeEvent): void => {
    if (e.target.checked) {
      props.onChange([
        {
          lookupStep: {
            type: LookupStepType.FILTER_BY_ORIGIN,
          } as FilterByOriginLookupStepSpec,
        } as ModifierLookupStepInfo,
      ]);
    } else {
      props.onChange([]);
    }
  };

  const idRef = useRef(_.uniqueId(`filter-by-origin-${props.stepName.replace(' ', '-')}`));

  const stepNameLower = props.stepName.toLowerCase();
  const tooltipText = props.isProcessOutputContext
    ? `This option will filter ${stepNameLower} to only include those that were created in the same entry as the output. That entry being the execution entry of the task that is associated with the output.`
    : `This option will filter ${stepNameLower} to only include those that were created in the current entry or document`;
  const checkboxLabel = props.isProcessOutputContext
    ? `Filter ${stepNameLower} to those created in the output's task execution entry`
    : `Filter ${stepNameLower} to those created in this entry or document`;

  return (
    <div
      className={css(styles.filterByOriginWrapper)}
      {...getTestProps({component: 'FilterByOriginForm', element: 'root'})}
    >
      <CheckboxInput
        id={idRef.current}
        label={checkboxLabel}
        disabled={props.disabled}
        checked={filterByOrigin}
        onChange={checkboxOnChange}
      />
      <Tooltip title={tooltipText}>
        <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
      </Tooltip>
    </div>
  );
}

type LookupStepModifierFormsByType = {
  [type in ModifierLookupStepType]?: (props: DummyModifierFormProps<ModifierLookupStepInfo>) => JSX.Element;
};

const lookupStepModifierFormsByType = (): LookupStepModifierFormsByType => ({
  [LookupStepType.FILTER_BY_ORIGIN]: (props: DummyModifierFormProps<FilterByOriginLookupStepInfo>) => (
    <FilterByOriginForm {...props} />
  ),
});

const getModifiedStepInfosForType = (
  stepType: ModifierLookupStepType,
  modifierLookupStepInfos?: Array<ModifierLookupStepInfo>
): Array<ModifierLookupStepInfo> =>
  modifierLookupStepInfos ? modifierLookupStepInfos.filter((info) => info.lookupStep.type === stepType) : [];

export interface LookupStepModifierFormProps {
  lookupStepInfo: LookupStepInfo;
  intermediateLookupStepTypes: Array<LookupStepType>;
  handleSetLookupStep: (lookupStep: LookupStepInfo) => void;
  disabled: boolean;
  isProcessOutputContext: boolean;
}

function getModifierStepTypes(
  lookupStepInfo: LookupStepInfo,
  allowedLookupStepTypes: Array<LookupStepType>
): Array<ModifierLookupStepType> {
  const modifierStepTypes: Array<ModifierLookupStepType> = [];
  const lookupStepInfoWithType = getLookupStepInfoWithType(lookupStepInfo);
  const lookupStep = lookupStepInfoWithType ? lookupStepInfoWithType.lookupStep : lookupStepInfo.lookupStep;
  if (lookupStep.type === LookupStepType.RESULTS) {
    modifierStepTypes.push(LookupStepType.FILTER_BY_ORIGIN);
  }
  return modifierStepTypes.filter((stepType) => allowedLookupStepTypes.includes(stepType));
}

export default function LookupStepModifierForm(props: LookupStepModifierFormProps): JSX.Element {
  // To add more types, need to store infos by type in state and include them in onChange callback
  const formsByType = lookupStepModifierFormsByType();

  const validModifierStepTypes = getModifierStepTypes(
    props.lookupStepInfo,
    props.intermediateLookupStepTypes
  );

  return (
    <div className={css(styles.modifierForm)}>
      {validModifierStepTypes.map((stepType) => {
        const form = formsByType[stepType];
        // form must exist since list of types are derived from its keys
        return form!({
          lookupStepInfos: getModifiedStepInfosForType(stepType, props.lookupStepInfo.modifierLookupSteps),
          stepName: props.lookupStepInfo.name,
          disabled: props.disabled || !props.lookupStepInfo,
          isProcessOutputContext: props.isProcessOutputContext,
          onChange: (infos: Array<ModifierLookupStepInfo>) => {
            props.handleSetLookupStep({
              ...props.lookupStepInfo,
              modifierLookupSteps: infos,
            } as LookupStepInfo);
          },
          key: stepType,
        });
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  modifierForm: {
    marginTop: '3px',
  },
  infoIcon: {
    marginLeft: '4px',
    marginTop: '2px',
    color: GRAY_DARK,
  },
  filterByOriginWrapper: {
    display: 'flex',
  },
});
